import React, { useState } from 'react';
import { ChevronDown, ChevronRight, TrendingUp, Clock, AlertCircle } from 'lucide-react';

const PriorityUseCaseCard = ({ useCase, actions, onActionUpdate }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Filter actions for this use case
  const useCaseActions = actions.filter(action => action.use_case_id === useCase.use_case_id);
  
  // Group actions by priority
  const highPriority = useCaseActions.filter(a => a.priority === 'High');
  const mediumPriority = useCaseActions.filter(a => a.priority === 'Medium');
  const lowPriority = useCaseActions.filter(a => a.priority === 'Low');

  // Count completed actions
  const completedCount = useCaseActions.filter(a => a.status === 'completed').length;
  const openCount = useCaseActions.filter(a => a.status === 'open').length;
  const inProgressCount = useCaseActions.filter(a => a.status === 'in-progress').length;

  // Get priority color
  const getPriorityColor = (label) => {
    switch (label) {
      case 'High': return 'text-red-600 bg-red-50';
      case 'Medium': return 'text-yellow-600 bg-yellow-50';
      case 'Low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const handleStatusToggle = async (action) => {
    const newStatus = action.status === 'completed' ? 'open' : 'completed';
    await onActionUpdate(action.action_id, newStatus);
  };

  const ActionItem = ({ action }) => (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
      <input
        type="checkbox"
        checked={action.status === 'completed'}
        onChange={() => handleStatusToggle(action)}
        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <div className="flex-1 min-w-0">
        <div className={`font-medium ${action.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
          {action.label}
        </div>
        {action.description && (
          <div className="text-sm text-gray-600 mt-1">{action.description}</div>
        )}
        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="font-medium">Role:</span> {action.role}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {action.due_window}
          </span>
          {action.dependencies && action.dependencies.length > 0 && (
            <span className="flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {action.dependencies.length} dependencies
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const ActionGroup = ({ title, actions, color }) => {
    if (actions.length === 0) return null;
    
    return (
      <div className="space-y-2">
        <div className={`text-sm font-semibold uppercase tracking-wide ${color} px-2 py-1 rounded inline-block`}>
          {title} ({actions.length})
        </div>
        <div className="space-y-2">
          {actions.map(action => (
            <ActionItem key={action.action_id} action={action} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow">
      {/* Card Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
      >
        <div className="flex items-start gap-4 flex-1 text-left">
          {/* Rank Badge */}
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-lg">
            {useCase.rank}
          </div>

          {/* Use Case Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {useCase.name}
            </h3>
            
            {/* Stats Row */}
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <TrendingUp className="h-4 w-4" />
                <span className="font-medium">Score: {useCase.total_score?.toFixed(1)}</span>
              </div>
              <span>•</span>
              <span>{useCaseActions.length} Actions</span>
              {highPriority.length > 0 && (
                <>
                  <span>•</span>
                  <span className="text-red-600 font-medium">
                    {highPriority.length} High Priority
                  </span>
                </>
              )}
            </div>

            {/* Progress Bar */}
            {useCaseActions.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                  <span>{completedCount} of {useCaseActions.length} completed</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${(completedCount / useCaseActions.length) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Expand Icon */}
        <div className="flex-shrink-0 ml-4">
          {isExpanded ? (
            <ChevronDown className="h-6 w-6 text-gray-400" />
          ) : (
            <ChevronRight className="h-6 w-6 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-4 border-t border-gray-200 pt-4">
          {useCaseActions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p>No actions yet for this use case</p>
            </div>
          ) : (
            <>
              <ActionGroup title="⚡ High Priority" actions={highPriority} color="text-red-700" />
              <ActionGroup title="📋 Medium Priority" actions={mediumPriority} color="text-yellow-700" />
              <ActionGroup title="📌 Low Priority" actions={lowPriority} color="text-green-700" />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PriorityUseCaseCard;